import { Document, Page, Text, View, StyleSheet, Link } from '@react-pdf/renderer'
import type { Resume, ResumeSectionKey } from '../../../types'
import {
  contactInline,
  joinDateRange,
  getSectionOrder,
  labelFor,
  displayUrl,
  BULLET,
  pdfMetadata,
  pdfFontFamily,
  previewFontFamily,
  type PdfFontFamily,
} from './shared'

const makeStyles = (font: PdfFontFamily) =>
  StyleSheet.create({
    page: { paddingTop: 40, paddingBottom: 50, paddingHorizontal: 48, fontSize: 10.5, color: '#111827', fontFamily: font.regular },
    headerName: { fontSize: 22, textAlign: 'center', fontFamily: font.bold },
    contactLine: { fontSize: 9.5, color: '#374151', marginTop: 4, textAlign: 'center' },
    divider: { borderBottomWidth: 0.75, borderBottomColor: '#111827', marginVertical: 10 },
    sectionTitle: { fontSize: 11.5, fontFamily: font.bold, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 4, borderBottomWidth: 0.5, borderBottomColor: '#9ca3af', paddingBottom: 2 },
    section: { marginBottom: 10 },
    itemHeader: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 1 },
    itemTitle: { fontSize: 11, fontFamily: font.bold },
    itemMeta: { fontSize: 9.5, color: '#374151', fontFamily: font.italic },
    bulletRow: { flexDirection: 'row', marginTop: 2 },
    bulletDot: { width: 10, fontSize: 10.5 },
    bulletText: { flex: 1, fontSize: 10.5, lineHeight: 1.4 },
    paragraph: { fontSize: 10.5, lineHeight: 1.45 },
    pageNumber: { position: 'absolute', bottom: 24, left: 0, right: 0, textAlign: 'center', fontSize: 8.5, color: '#6b7280' },
    linkInline: { textDecoration: 'none', color: '#1d4ed8' },
  })

type ClassicStyles = ReturnType<typeof makeStyles>

function renderSection(key: ResumeSectionKey, resume: Resume, styles: ClassicStyles, font: PdfFontFamily) {
  switch (key) {
    case 'summary':
      if (!resume.summary) return null
      return (
        <View key={key} style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'summary')}</Text>
          <Text style={styles.paragraph}>{resume.summary}</Text>
        </View>
      )
    case 'experience':
      if (resume.experience.length === 0) return null
      return (
        <View key={key} style={styles.section}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'experience')}</Text>
          {resume.experience.map((exp) => (
            <View key={exp.id} style={{ marginBottom: 6 }} wrap={false}>
              <View style={styles.itemHeader}>
                <Text style={styles.itemTitle}>{exp.company || exp.role}</Text>
                <Text style={styles.itemMeta}>{joinDateRange(exp.startDate, exp.endDate)}</Text>
              </View>
              <Text style={styles.itemMeta}>{[exp.role, exp.location].filter(Boolean).join(' · ')}</Text>
              {exp.bullets.filter(Boolean).map((b, i) => (
                <View key={i} style={styles.bulletRow}>
                  <Text style={styles.bulletDot}>{BULLET}</Text>
                  <Text style={styles.bulletText}>{b}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>
      )
    case 'education':
      if (resume.education.length === 0) return null
      return (
        <View key={key} style={styles.section}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'education')}</Text>
          {resume.education.map((e) => (
            <View key={e.id} style={{ marginBottom: 4 }} wrap={false}>
              <View style={styles.itemHeader}>
                <Text style={styles.itemTitle}>{e.institution}</Text>
                <Text style={styles.itemMeta}>{joinDateRange(e.startDate, e.endDate)}</Text>
              </View>
              <Text style={styles.itemMeta}>{[e.degree, e.field, e.location].filter(Boolean).join(' · ')}</Text>
              {e.details ? <Text style={styles.paragraph}>{e.details}</Text> : null}
            </View>
          ))}
        </View>
      )
    case 'skills':
      if (resume.skills.length === 0) return null
      return (
        <View key={key} style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'skills')}</Text>
          {resume.skills.map((g) => (
            <Text key={g.id} style={styles.paragraph}>
              <Text style={{ fontFamily: font.bold }}>{g.category}: </Text>
              {g.items.join(', ')}
            </Text>
          ))}
        </View>
      )
    case 'projects':
      if (resume.projects.length === 0) return null
      return (
        <View key={key} style={styles.section}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'projects')}</Text>
          {resume.projects.map((p) => (
            <View key={p.id} style={{ marginBottom: 4 }} wrap={false}>
              <View style={styles.itemHeader}>
                <Text style={styles.itemTitle}>{p.name}</Text>
                {p.url ? (
                  <Link src={p.url} style={[styles.itemMeta, styles.linkInline]}>{displayUrl(p.url)}</Link>
                ) : null}
              </View>
              {p.description ? <Text style={styles.paragraph}>{p.description}</Text> : null}
              {p.bullets.filter(Boolean).map((b, i) => (
                <View key={i} style={styles.bulletRow}>
                  <Text style={styles.bulletDot}>{BULLET}</Text>
                  <Text style={styles.bulletText}>{b}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>
      )
    case 'certifications':
      if (resume.certifications.length === 0) return null
      return (
        <View key={key} style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'certifications')}</Text>
          {resume.certifications.map((c) => (
            <Text key={c.id} style={styles.paragraph}>
              {c.name}{c.issuer ? ` · ${c.issuer}` : ''}{c.date ? ` · ${c.date}` : ''}
            </Text>
          ))}
        </View>
      )
    case 'languages':
      if (resume.languages.length === 0) return null
      return (
        <View key={key} style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'languages')}</Text>
          <Text style={styles.paragraph}>
            {resume.languages.map((l) => l.proficiency ? `${l.name} (${l.proficiency})` : l.name).join(' · ')}
          </Text>
        </View>
      )
    case 'customSections':
      return resume.customSections.map((s) => (
        <View key={s.id} style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>{s.title}</Text>
          <Text style={styles.paragraph}>{s.body}</Text>
        </View>
      ))
  }
}

export function ClassicPdf({ resume }: { resume: Resume }) {
  const contacts = contactInline(resume.contact)
  const meta = pdfMetadata(resume)
  const order = getSectionOrder(resume)
  const font = pdfFontFamily(resume, 'serif')
  const styles = makeStyles(font)
  return (
    <Document title={meta.title} author={meta.author} subject={meta.subject}>
      <Page size="A4" style={styles.page}>
        <View>
          <Text style={styles.headerName}>{resume.contact.fullName || ' '}</Text>
          {resume.contact.headline ? <Text style={styles.contactLine}>{resume.contact.headline}</Text> : null}
          {contacts.length > 0 && <Text style={styles.contactLine}>{contacts.join(' · ')}</Text>}
        </View>
        <View style={styles.divider} />

        {order.map((key) => renderSection(key, resume, styles, font))}

        <Text
          style={styles.pageNumber}
          fixed
          render={({ pageNumber, totalPages }) => (totalPages > 1 ? `${pageNumber} / ${totalPages}` : '')}
        />
      </Page>
    </Document>
  )
}

export function ClassicPreview({ resume }: { resume: Resume }) {
  const contacts = contactInline(resume.contact)
  const order = getSectionOrder(resume)
  return (
    <div className="bg-white text-slate-900 shadow-sm rounded-md p-10 text-[12px] leading-snug" style={{ fontFamily: previewFontFamily(resume, 'serif') }}>
      <div className="text-center">
        <div className="text-2xl font-bold">{resume.contact.fullName || '\u00a0'}</div>
        {resume.contact.headline && <div className="text-slate-700">{resume.contact.headline}</div>}
        {contacts.length > 0 && <div className="text-slate-700 text-[11px]">{contacts.join(' · ')}</div>}
      </div>
      <hr className="my-3 border-slate-700" />

      {order.map((key) => {
        switch (key) {
          case 'summary':
            if (!resume.summary) return null
            return <ClassicSection key={key} title={labelFor(resume, 'summary')}><p>{resume.summary}</p></ClassicSection>
          case 'experience':
            if (resume.experience.length === 0) return null
            return (
              <ClassicSection key={key} title={labelFor(resume, 'experience')}>
                {resume.experience.map((e) => (
                  <div key={e.id} className="mb-2">
                    <div className="flex justify-between"><div className="font-bold">{e.company || e.role}</div><div className="italic text-slate-600 text-[11px]">{joinDateRange(e.startDate, e.endDate)}</div></div>
                    <div className="italic text-slate-600 text-[11px]">{[e.role, e.location].filter(Boolean).join(' · ')}</div>
                    <ul className="list-disc pl-5 mt-0.5">{e.bullets.filter(Boolean).map((b, i) => <li key={i}>{b}</li>)}</ul>
                  </div>
                ))}
              </ClassicSection>
            )
          case 'education':
            if (resume.education.length === 0) return null
            return (
              <ClassicSection key={key} title={labelFor(resume, 'education')}>
                {resume.education.map((e) => (
                  <div key={e.id} className="mb-1.5">
                    <div className="flex justify-between"><div className="font-bold">{e.institution}</div><div className="italic text-slate-600 text-[11px]">{joinDateRange(e.startDate, e.endDate)}</div></div>
                    <div className="italic text-slate-600 text-[11px]">{[e.degree, e.field, e.location].filter(Boolean).join(' · ')}</div>
                    {e.details && <p>{e.details}</p>}
                  </div>
                ))}
              </ClassicSection>
            )
          case 'skills':
            if (resume.skills.length === 0) return null
            return (
              <ClassicSection key={key} title={labelFor(resume, 'skills')}>
                {resume.skills.map((g) => <p key={g.id}><b>{g.category}:</b> {g.items.join(', ')}</p>)}
              </ClassicSection>
            )
          case 'projects':
            if (resume.projects.length === 0) return null
            return (
              <ClassicSection key={key} title={labelFor(resume, 'projects')}>
                {resume.projects.map((p) => (
                  <div key={p.id} className="mb-1.5">
                    <div className="flex justify-between">
                      <div className="font-bold">{p.name}</div>
                      {p.url && <a className="text-[11px] text-blue-700" href={p.url}>{displayUrl(p.url)}</a>}
                    </div>
                    {p.description && <p>{p.description}</p>}
                    <ul className="list-disc pl-5">{p.bullets.filter(Boolean).map((b, i) => <li key={i}>{b}</li>)}</ul>
                  </div>
                ))}
              </ClassicSection>
            )
          case 'certifications':
            if (resume.certifications.length === 0) return null
            return (
              <ClassicSection key={key} title={labelFor(resume, 'certifications')}>
                {resume.certifications.map((c) => <p key={c.id}>{c.name}{c.issuer ? ` · ${c.issuer}` : ''}{c.date ? ` · ${c.date}` : ''}</p>)}
              </ClassicSection>
            )
          case 'languages':
            if (resume.languages.length === 0) return null
            return (
              <ClassicSection key={key} title={labelFor(resume, 'languages')}>
                <p>{resume.languages.map((l) => l.proficiency ? `${l.name} (${l.proficiency})` : l.name).join(' · ')}</p>
              </ClassicSection>
            )
          case 'customSections':
            return resume.customSections.map((s) => (
              <ClassicSection key={s.id} title={s.title}><p className="whitespace-pre-line">{s.body}</p></ClassicSection>
            ))
        }
      })}

      {!resume.summary && resume.experience.length === 0 && resume.projects.length === 0 && resume.education.length === 0 && (
        <p className="text-slate-400 italic text-[11px] mt-4">Add content on the left to fill out your resume.</p>
      )}
    </div>
  )
}

function ClassicSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <div className="text-[11.5px] font-bold uppercase tracking-wider border-b border-slate-400 pb-0.5 mb-1">{title}</div>
      <div>{children}</div>
    </div>
  )
}
